import React, { useEffect, useState } from "react";
import useFirestoreCollection from "../utils/useFirestoreCollection";
import "../Styles/OpenStatus.css";

// The restaurant lives in Rhodes, so the open/closed status is always
// computed in the restaurant's local timezone, regardless of where the
// visitor's device happens to be.
const RESTAURANT_TZ = "Europe/Athens";

const DAY_NAMES = [
  "Sunday",
  "Monday",
  "Tuesday",
  "Wednesday",
  "Thursday",
  "Friday",
  "Saturday",
];

// Returns the current weekday name and minutes-since-midnight in the
// restaurant's timezone (handles DST automatically).
function getNowInRestaurantTz() {
  const now = new Date();
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: RESTAURANT_TZ,
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(now);

  const get = (type) => parts.find((p) => p.type === type)?.value;
  const weekday = get("weekday");
  let hour = parseInt(get("hour"), 10);
  const minute = parseInt(get("minute"), 10);
  if (Number.isNaN(hour) || hour === 24) hour = 0; // guard against "24:00"

  return { weekday, minutes: hour * 60 + minute };
}

// Parses a single opening range like "12:00 - 23:00" (tolerant of spacing,
// hyphen/en-dash/em-dash/"to", and a "." time separator). Returns minutes
// since midnight for start/end. If end <= start the window is treated as
// crossing midnight, so end is pushed into the next day (> 1440).
function parseRange(opening) {
  if (!opening) return null;
  const m = String(opening).match(
    /(\d{1,2})[:.](\d{2})\s*(?:-|–|—|to|έως)\s*(\d{1,2})[:.](\d{2})/i
  );
  if (!m) return null;

  const start = parseInt(m[1], 10) * 60 + parseInt(m[2], 10);
  let end = parseInt(m[3], 10) * 60 + parseInt(m[4], 10);
  if (
    [start, end].some((v) => Number.isNaN(v)) ||
    parseInt(m[1], 10) > 23 ||
    parseInt(m[3], 10) > 23
  ) {
    return null;
  }
  if (end <= start) end += 24 * 60; // overnight, e.g. 18:00 - 01:00
  return { start, end };
}

function formatMinutes(mins) {
  const h = Math.floor(mins / 60) % 24;
  const m = mins % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function findDay(hours, name) {
  if (!name) return null;
  const target = name.trim().toLowerCase();
  return (
    hours.find((d) => (d.day || "").trim().toLowerCase() === target) || null
  );
}

// Decides the current status from the hours data.
//   { state: "open" }
//   { state: "closed", opensAt: "18:00" }  // reopens later the same day
//   { state: "closed" }                     // closed for the rest of the day
//   null                                    // can't determine -> render nothing
function computeStatus(hours) {
  if (!Array.isArray(hours) || hours.length === 0) return null;

  const { weekday, minutes } = getNowInRestaurantTz();
  const todayIdx = DAY_NAMES.indexOf(weekday);
  if (todayIdx === -1) return null;

  const today = findDay(hours, weekday);
  const yesterday = findDay(hours, DAY_NAMES[(todayIdx + 6) % 7]);

  // We need at least today's record to say anything sensible.
  if (!today) return null;

  // Open right now within today's window?
  if (today.isOpen) {
    const r = parseRange(today.opening);
    if (r && minutes >= r.start && minutes < r.end) {
      return { state: "open" };
    }
  }

  // Still inside yesterday's late-night service that runs past midnight?
  if (yesterday && yesterday.isOpen) {
    const r = parseRange(yesterday.opening);
    if (r && r.end > 24 * 60 && minutes < r.end - 24 * 60) {
      return { state: "open" };
    }
  }

  // Closed now. Does it reopen later today?
  if (today.isOpen) {
    const r = parseRange(today.opening);
    if (r && minutes < r.start) {
      return { state: "closed", opensAt: formatMinutes(r.start) };
    }
  }

  return { state: "closed" };
}

const OpenStatus = () => {
  const { loading: hoursLoading, data: hours } = useFirestoreCollection(
    "hours"
  );
  const { loading: adminLoading, data: adminData } = useFirestoreCollection(
    "administration"
  );

  // Re-evaluate every minute so the pill flips without a page reload.
  const [, setTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (hoursLoading || adminLoading) return null;

  // Seasonal closure: hide the pill entirely while the restaurant is closed
  // for the winter.
  const isWinter =
    adminData && adminData.length > 0 ? adminData[0].isWinter : false;
  if (isWinter) return null;

  const status = computeStatus(hours);
  if (!status) return null;

  const isOpen = status.state === "open";
  const label = isOpen
    ? "Open now"
    : status.opensAt
    ? `Closed · Opens at ${status.opensAt}`
    : "Closed";

  return (
    <div className="open-status-bar">
      <span
        className={`open-status open-status--${isOpen ? "open" : "closed"}`}
        role="status"
        aria-live="polite"
      >
        <span className="open-status__dot" aria-hidden="true"></span>
        <span className="open-status__text">{label}</span>
      </span>
    </div>
  );
};

export default OpenStatus;
